import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Button, Stack,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, InputAdornment, Divider, Grid, Switch,
  Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Alert, CircularProgress, Backdrop, Tooltip, Checkbox
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { RootState } from '../store';
import { showSnackbar } from '../store/slices/uiSlice';
import apiService from '../services/api.service';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import DescriptionIcon from '@mui/icons-material/Description';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSubcontractProjects } from '../hooks/useSubcontractProjects';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import ActionStatusDialog, { ActionStatusDialogState } from '../components/common/ActionStatusDialog';

ModuleRegistry.registerModules([AllCommunityModule]);

type DocumentTypeKey =
  | 'PerformanceBond'
  | 'AllRiskInsurance'
  | 'WorkmensCompInsurance'
  | 'TaxClearanceCert'
  | 'AdvancePaymentGuarantee'
  | 'RetentionGuarantee'
  | 'SubcontractAgreement'
  | 'LetterOfAuthorization'
  | 'CivilIDCopies'
  | 'BankAccountDetails';

interface DocumentCardState {
  received: boolean;
  uploading: boolean;
  attachments: any[];
}

interface DeductionReleaseLine {
  key: string;
  itemCode: string;
  itemName: string;
  accountCode: string;
  withheldAmount: number;
  releasedAmount: number;
  remainingAmount: number;
  selected: boolean;
  releaseAmount: number;
}

const DOCUMENT_TYPES: { key: DocumentTypeKey; label: string; requiredField: string }[] = [
  { key: 'PerformanceBond', label: 'Performance Bond', requiredField: 'performancebondrequired' },
  { key: 'AllRiskInsurance', label: 'All Risk Insurance', requiredField: 'allriskinsurancerequired' },
  { key: 'WorkmensCompInsurance', label: "Workmen's Compensation Insurance", requiredField: 'workmenscompensationrequired' },
  { key: 'TaxClearanceCert', label: 'Tax Clearance Certificate', requiredField: 'taxclearancecertrequired' },
  { key: 'AdvancePaymentGuarantee', label: 'Advance Payment Guarantee', requiredField: 'advancepaymentguaranteerequired' },
  { key: 'RetentionGuarantee', label: 'Retention Guarantee', requiredField: 'retentionguaranteerequired' },
  { key: 'SubcontractAgreement', label: 'Subcontract Agreement', requiredField: 'subcontractagreementrequired' },
  { key: 'LetterOfAuthorization', label: 'Letter of Authorization', requiredField: 'letterofauthorizationrequired' },
  { key: 'CivilIDCopies', label: 'Civil ID Copies', requiredField: 'civilidcopiesrequired' },
  { key: 'BankAccountDetails', label: 'Bank Account Details', requiredField: 'bankaccountdetailsrequired' },
];

const goldButtonSx = {
  bgcolor: '#F1C232',
  color: '#000',
  '&:hover': { bgcolor: '#d4a828' },
  fontWeight: 600,
};

const sectionHeaderSx = {
  fontWeight: 700,
  fontSize: '0.95rem',
  color: '#1a237e',
  mb: 1,
  mt: 2,
};

const readOnlyFieldSx = {
  '& .MuiInputBase-input': { bgcolor: '#f5f5f5' },
};

const statusChipColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' => {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'success';
  if (s === 'completed') return 'info';
  if (s === 'cancelled') return 'error';
  if (s === 'draft') return 'warning';
  return 'default';
};

const SubcontractDocuments: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { companyId: selectedCompanyId, projects: projectOptions } = useSubcontractProjects();

  // --- Search filters ---
  const [filterProjectCode, setFilterProjectCode] = useState('');
  const [filterProjectName, setFilterProjectName] = useState('');
  const [filterVendorCode, setFilterVendorCode] = useState('');
  const [filterVendorName, setFilterVendorName] = useState('');
  const [filterContractNumber, setFilterContractNumber] = useState('');

  // --- LOV data ---
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);
  const allowedModules: string[] = Array.isArray((user as any)?.allowedmodules) ? ((user as any)?.allowedmodules as string[]) : [];
  const canViewPaymentCertificates = allowedModules.includes('payment-certificate');

  // --- Picker states ---
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [vendorPickerOpen, setVendorPickerOpen] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');

  // --- Search results ---
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);

  // --- Selected subcontract ---
  const [selectedSC, setSelectedSC] = useState<any | null>(null);
  const [loadingSC, setLoadingSC] = useState(false);

  // --- Document received states ---
  const defaultDocState = (): DocumentCardState => ({ received: false, uploading: false, attachments: [] });
  const [docStates, setDocStates] = useState<Record<DocumentTypeKey, DocumentCardState>>({
    PerformanceBond: defaultDocState(),
    AllRiskInsurance: defaultDocState(),
    WorkmensCompInsurance: defaultDocState(),
    TaxClearanceCert: defaultDocState(),
    AdvancePaymentGuarantee: defaultDocState(),
    RetentionGuarantee: defaultDocState(),
    SubcontractAgreement: defaultDocState(),
    LetterOfAuthorization: defaultDocState(),
    CivilIDCopies: defaultDocState(),
    BankAccountDetails: defaultDocState(),
  });
  const fileInputRefs = useRef<Record<DocumentTypeKey, HTMLInputElement | null>>({
    PerformanceBond: null,
    AllRiskInsurance: null,
    WorkmensCompInsurance: null,
    TaxClearanceCert: null,
    AdvancePaymentGuarantee: null,
    RetentionGuarantee: null,
    SubcontractAgreement: null,
    LetterOfAuthorization: null,
    CivilIDCopies: null,
    BankAccountDetails: null,
  });

  // --- Payment certificates ---
  const [paymentCerts, setPaymentCerts] = useState<any[]>([]);
  const [loadingPCs, setLoadingPCs] = useState(false);

  // --- PC detail dialog ---
  const [pcDetailOpen, setPcDetailOpen] = useState(false);
  const [pcDetailData, setPcDetailData] = useState<any>(null);
  const [pcDetailLoading, setPcDetailLoading] = useState(false);

  // --- SAP posting ---
  const [sapPostConfirmOpen, setSapPostConfirmOpen] = useState(false);
  const [sapPostTarget, setSapPostTarget] = useState<{ scId: number; pcId: number } | null>(null);
  const [sapPosting, setSapPosting] = useState(false);
  const [actionStatus, setActionStatus] = useState<ActionStatusDialogState>({
    open: false,
    status: 'processing',
    title: '',
    message: '',
  });

  // --- Deduction release AP invoice ---
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releasePosting, setReleasePosting] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState<{ scId: number; pcId: number } | null>(null);
  const [releasePreview, setReleasePreview] = useState<any>(null);
  const [releaseLines, setReleaseLines] = useState<DeductionReleaseLine[]>([]);
  const userRole = String((user as any)?.role || (user as any)?.userrole || '');
  const canReleaseDeductions = userRole === 'FinanceManager' || userRole === 'Admin';

  // --- Filtered options ---
  const filteredProjectOptions = useMemo(() => {
    if (!projectSearchQuery.trim()) return projectOptions;
    const query = projectSearchQuery.toLowerCase();
    return projectOptions.filter((project) =>
      project.code.toLowerCase().includes(query) || project.name.toLowerCase().includes(query)
    );
  }, [projectOptions, projectSearchQuery]);

  const filteredVendorOptions = useMemo(() => {
    if (!vendorSearchQuery.trim()) return vendorOptions;
    const q = vendorSearchQuery.toLowerCase();
    return vendorOptions.filter((v: any) =>
      (v.CardCode || v.cardcode || '').toLowerCase().includes(q) ||
      (v.CardName || v.cardname || '').toLowerCase().includes(q)
    );
  }, [vendorOptions, vendorSearchQuery]);

  // --- Search subcontracts ---
  const executeSearch = useCallback(async (options?: { silentIfEmpty?: boolean }) => {
    setSearching(true);
    setSelectedSC(null);
    setPaymentCerts([]);
    try {
      const params: any = {};
      if (filterProjectCode) params.projectCode = filterProjectCode;
      if (filterProjectName) params.projectName = filterProjectName;
      if (filterVendorCode) params.vendorCode = filterVendorCode;
      if (filterVendorName) params.vendorName = filterVendorName;
      if (filterContractNumber) params.contractNumber = filterContractNumber;
      const res = await apiService.listSubcontracts(params);
      const list = (res as any)?.data || res || [];
      setSearchResults(Array.isArray(list) ? list : []);
      if (!options?.silentIfEmpty && Array.isArray(list) && list.length === 0) {
        dispatch(showSnackbar({ message: 'No subcontracts found matching the filters', severity: 'info' }));
      }
    } catch (err: any) {
      const detail = err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Unknown error';
      console.error('Search subcontracts error:', err?.response?.data || err);
      dispatch(showSnackbar({ message: `Failed to search subcontracts: ${detail}`, severity: 'error' }));
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [filterProjectCode, filterProjectName, filterVendorCode, filterVendorName, filterContractNumber, dispatch]);

  const handleSearch = useCallback(async () => {
    await executeSearch();
  }, [executeSearch]);

  // --- Load LOV data and initial subcontract list ---
  useEffect(() => {
    const loadOptionsAndResults = async () => {
      try {
        if (!selectedCompanyId) {
          setVendorOptions([]);
          setSearchResults([]);
          setSelectedSC(null);
          return;
        }
        const vendorRes = await apiService.get('/sap/vendors', { companyId: selectedCompanyId });
        setVendorOptions((vendorRes as any)?.data || vendorRes || []);
        await executeSearch({ silentIfEmpty: true });
      } catch (error) {
        console.error('Error loading options:', error);
      }
    };

    void loadOptionsAndResults();
  }, [executeSearch, selectedCompanyId]);

  // --- Load full subcontract detail ---
  const loadSubcontract = useCallback(async (scId: number) => {
    setLoadingSC(true);
    try {
      const res = await apiService.getSubcontract(scId);
      const sc = (res as any)?.data || res || null;
      setSelectedSC(sc);

      // Initialize document received states from subcontract documents
      const docs: any[] = sc?.documents || [];
      const newDocStates: Record<DocumentTypeKey, DocumentCardState> = {
        PerformanceBond: defaultDocState(),
        AllRiskInsurance: defaultDocState(),
        WorkmensCompInsurance: defaultDocState(),
        TaxClearanceCert: defaultDocState(),
        AdvancePaymentGuarantee: defaultDocState(),
        RetentionGuarantee: defaultDocState(),
        SubcontractAgreement: defaultDocState(),
        LetterOfAuthorization: defaultDocState(),
        CivilIDCopies: defaultDocState(),
        BankAccountDetails: defaultDocState(),
      };
      for (const doc of docs) {
        const dtype = doc.documenttype as DocumentTypeKey;
        if (dtype && newDocStates[dtype]) {
          newDocStates[dtype].received = !!doc.received;
        }
      }

      // Load attachments
      try {
        const attRes = await apiService.getDocumentAttachments(scId);
        const atts = (attRes as any)?.data || attRes || [];
        for (const att of atts) {
          const dtype = att.documenttype as DocumentTypeKey;
          if (dtype && newDocStates[dtype]) {
            newDocStates[dtype].attachments.push(att);
          }
        }
      } catch {
        // Attachments load failed silently
      }

      setDocStates(newDocStates);

      // Load payment certificates only when the user has that module access
      if (canViewPaymentCertificates) {
        setLoadingPCs(true);
        try {
          const pcRes = await apiService.getPaymentCertificates(scId);
          const pcs = (pcRes as any)?.data || pcRes || [];
          setPaymentCerts(Array.isArray(pcs) ? pcs : []);
        } catch (pcErr: any) {
          console.error('Failed to load payment certificates:', pcErr?.response?.data || pcErr?.message || pcErr);
          setPaymentCerts([]);
        } finally {
          setLoadingPCs(false);
        }
      } else {
        setPaymentCerts([]);
        setLoadingPCs(false);
      }
    } catch {
      dispatch(showSnackbar({ message: 'Failed to load subcontract details', severity: 'error' }));
      setSelectedSC(null);
    } finally {
      setLoadingSC(false);
    }
  }, [canViewPaymentCertificates, dispatch]);

  // --- Document received toggle ---
  const handleDocumentReceivedToggle = useCallback(async (docType: DocumentTypeKey, received: boolean) => {
    if (!selectedSC) return;
    const scId = selectedSC.subcontractid;
    try {
      await apiService.updateDocumentReceived(scId, docType, received);
      setDocStates((prev) => ({
        ...prev,
        [docType]: { ...prev[docType], received },
      }));
      dispatch(showSnackbar({ message: `${docType} received status updated`, severity: 'success' }));
    } catch (err: any) {
      const detail = err?.response?.data?.message || err?.message || 'Failed to update document received status';
      dispatch(showSnackbar({ message: detail, severity: 'error' }));
    }
  }, [selectedSC, dispatch]);

  // --- File upload ---
  const handleFileUpload = useCallback(async (docType: DocumentTypeKey, files: FileList | null) => {
    if (!selectedSC || !files || files.length === 0) return;
    const scId = selectedSC.subcontractid;
    setDocStates((prev) => ({
      ...prev,
      [docType]: { ...prev[docType], uploading: true },
    }));
    try {
      await apiService.uploadDocumentAttachment(scId, docType, Array.from(files));
      // Reload attachments
      const attRes = await apiService.getDocumentAttachments(scId);
      const atts = (attRes as any)?.data || attRes || [];
      const docAtts = atts.filter((a: any) => a.documenttype === docType);
      setDocStates((prev) => ({
        ...prev,
        [docType]: { ...prev[docType], uploading: false, attachments: docAtts },
      }));
      dispatch(showSnackbar({ message: 'Attachment uploaded successfully', severity: 'success' }));
    } catch {
      dispatch(showSnackbar({ message: 'Failed to upload attachment', severity: 'error' }));
      setDocStates((prev) => ({
        ...prev,
        [docType]: { ...prev[docType], uploading: false },
      }));
    } finally {
      const ref = fileInputRefs.current[docType];
      if (ref) ref.value = '';
    }
  }, [selectedSC, dispatch]);

  // --- PC detail dialog ---
  const handleOpenPCDetail = useCallback(async (pcId: number, scId?: number) => {
    if (!pcId || !scId) return;
    setPcDetailOpen(true);
    setPcDetailLoading(true);
    try {
      const res: any = await apiService.getPaymentCertificateById(scId, pcId);
      setPcDetailData(res?.data || res);
    } catch {
      dispatch(showSnackbar({ message: 'Failed to load payment certificate details', severity: 'error' }));
      setPcDetailOpen(false);
    } finally {
      setPcDetailLoading(false);
    }
  }, [dispatch]);

  // --- SAP posting ---
  const handleSAPPost = useCallback(async () => {
    if (!sapPostTarget) return;
    setSapPosting(true);
    setSapPostConfirmOpen(false);
    setActionStatus({
      open: true,
      status: 'processing',
      title: 'Posting Payment Certificate to SAP',
      message: 'Creating the SAP AP document and related inventory transaction.',
      details: 'Please wait. Do not close or refresh this page.',
    });
    try {
      const response = await apiService.postPaymentCertificateToSAP(sapPostTarget.scId, sapPostTarget.pcId);
      const result = (response as any)?.data || response || {};
      const references = [
        result.invoiceDocNum || result.invoiceDocEntry
          ? `AP DocNum ${result.invoiceDocNum || '-'} / DocEntry ${result.invoiceDocEntry || '-'}`
          : '',
        result.grpoDocNum || result.grpoDocEntry
          ? `GRPO DocNum ${result.grpoDocNum || '-'} / DocEntry ${result.grpoDocEntry || '-'}`
          : '',
        result.goodsIssueDocNum || result.goodsIssueDocEntry
          ? `Goods Issue DocNum ${result.goodsIssueDocNum || '-'} / DocEntry ${result.goodsIssueDocEntry || '-'}`
          : '',
      ].filter(Boolean).join(' | ');
      setActionStatus({
        open: true,
        status: 'success',
        title: 'SAP Posting Completed',
        message: 'The payment certificate was posted to SAP successfully.',
        details: references || undefined,
      });
      setSapPostTarget(null);
      // Reload payment certificates
      if (selectedSC && canViewPaymentCertificates) {
        const pcRes = await apiService.getPaymentCertificates(selectedSC.subcontractid);
        const pcs = (pcRes as any)?.data || pcRes || [];
        setPaymentCerts(Array.isArray(pcs) ? pcs : []);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to post to SAP';
      setActionStatus({
        open: true,
        status: 'error',
        title: 'SAP Posting Failed',
        message: msg,
        details: err?.response?.status ? `HTTP status ${err.response.status}` : undefined,
      });
    } finally {
      setSapPosting(false);
    }
  }, [canViewPaymentCertificates, sapPostTarget, selectedSC]);

  const handleOpenDeductionRelease = useCallback(async (scId: number, pcId: number) => {
    setReleaseDialogOpen(true);
    setReleaseLoading(true);
    setReleaseTarget({ scId, pcId });
    setReleasePreview(null);
    setReleaseLines([]);
    try {
      const response = await apiService.getSubcontractDeductionReleasePreview(scId, pcId);
      const preview = (response as any)?.data || response;
      setReleasePreview(preview);
      setReleaseLines((Array.isArray(preview?.lines) ? preview.lines : []).map((line: any) => ({
        ...line,
        selected: Number(line.remainingAmount || 0) > 0,
        releaseAmount: Number(line.remainingAmount || 0),
      })));
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Unable to load deduction release';
      dispatch(showSnackbar({ message, severity: 'error' }));
      setReleaseDialogOpen(false);
      setReleaseTarget(null);
    } finally {
      setReleaseLoading(false);
    }
  }, [dispatch]);

  const handlePostDeductionRelease = useCallback(async () => {
    if (!releaseTarget) return;
    const lines = releaseLines
      .filter((line) => line.selected && Number(line.releaseAmount) > 0)
      .map((line) => ({ key: line.key, amount: Number(line.releaseAmount) }));
    if (lines.length === 0) {
      dispatch(showSnackbar({ message: 'Select at least one deduction amount to release', severity: 'warning' }));
      return;
    }
    setReleasePosting(true);
    setActionStatus({
      open: true,
      status: 'processing',
      title: 'Posting Deduction Release to SAP',
      message: `Creating one AP invoice for ${lines.length} selected deduction line${lines.length === 1 ? '' : 's'}.`,
      details: 'Please wait. Do not close or refresh this page.',
    });
    try {
      const response = await apiService.postSubcontractDeductionRelease(releaseTarget.scId, releaseTarget.pcId, lines);
      const result = (response as any)?.data || response;
      setActionStatus({
        open: true,
        status: 'success',
        title: 'Deduction Release Posted',
        message: 'The selected deductions were posted as one SAP AP invoice.',
        details: `AP DocNum ${result?.sapDocNum || '-'} / DocEntry ${result?.sapDocEntry || '-'}`,
      });
      setReleaseDialogOpen(false);
      setReleaseTarget(null);
      setReleasePreview(null);
      setReleaseLines([]);
      if (selectedSC && canViewPaymentCertificates) {
        const pcResponse = await apiService.getPaymentCertificates(selectedSC.subcontractid);
        const pcs = (pcResponse as any)?.data || pcResponse || [];
        setPaymentCerts(Array.isArray(pcs) ? pcs : []);
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Deduction release posting failed';
      setActionStatus({
        open: true,
        status: 'error',
        title: 'Deduction Release Failed',
        message,
        details: err?.response?.status ? `HTTP status ${err.response.status}` : undefined,
      });
    } finally {
      setReleasePosting(false);
    }
  }, [canViewPaymentCertificates, dispatch, releaseLines, releaseTarget, selectedSC]);

  const selectedReleaseTotal = useMemo(() => releaseLines.reduce(
    (sum, line) => sum + (line.selected ? Number(line.releaseAmount || 0) : 0),
    0
  ), [releaseLines]);

  const paymentCertificateSummary = useMemo(() => {
    const contractValue = Number(
      selectedSC?.revisedcontractvalue
      || selectedSC?.contractvalue
      || selectedSC?.subcontractvalue
      || 0
    );
    const postedNetAmount = paymentCerts.reduce((sum, pc) => (
      pc.sapinvoicedocentry || pc.sapinvoicedocnum
        ? sum + Number(pc.currentnetpayable || 0)
        : sum
    ), 0);
    const totalDeducted = paymentCerts.reduce((sum, pc) => sum + Number(pc.deductiontotalamount || 0), 0);
    const totalReleased = paymentCerts.reduce((sum, pc) => sum + Number(pc.deductionreleasedamount || 0), 0);
    const paidAmount = postedNetAmount + totalReleased;
    return {
      contractValue,
      paidAmount,
      pendingContractAmount: Math.max(0, contractValue - paidAmount),
      totalDeducted,
      totalReleased,
      pendingDeductions: Math.max(0, totalDeducted - totalReleased),
    };
  }, [paymentCerts, selectedSC]);

  const formatCertificateAmount = (value: number): string => Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const handleCopyPaymentCertificateTable = useCallback(async () => {
    if (paymentCerts.length === 0) {
      dispatch(showSnackbar({ message: 'No payment certificate rows are available to copy', severity: 'warning' }));
      return;
    }
    const clean = (value: unknown): string => String(value ?? '')
      .replace(/\t/g, ' ')
      .replace(/[\r\n]+/g, ' ')
      .trim();
    const amount = (value: unknown): string => Number(value || 0).toFixed(3);
    const summaryRows = [
      ['Contract Summary', 'Amount'],
      ['Contract Value', amount(paymentCertificateSummary.contractValue)],
      ['Paid / Posted Amount', amount(paymentCertificateSummary.paidAmount)],
      ['Pending Contract Amount', amount(paymentCertificateSummary.pendingContractAmount)],
      ['Total Withheld Deductions', amount(paymentCertificateSummary.totalDeducted)],
      ['Released Deductions', amount(paymentCertificateSummary.totalReleased)],
      ['Pending Deduction Release', amount(paymentCertificateSummary.pendingDeductions)],
    ];
    const headers = [
      'Payment No', 'Type', 'Date', 'Contract Value', 'Current Work Done', 'Net Payable',
      'Retention', 'Performance Bond', 'All Risk Insurance', "Workmen's Compensation",
      'Tax Clearance', 'Advance Payment Guarantee', 'Total Deducted', 'Released Deductions',
      'Pending Release', 'Deduction Status', 'Certificate Status', 'SAP Status',
      'GRPO DocNum', 'GRPO DocEntry', 'AP DocNum', 'AP DocEntry',
      'Goods Issue DocNum', 'Goods Issue DocEntry', 'Created By', 'Submitted By', 'Remarks',
    ];
    const rows = paymentCerts.map((pc: any) => {
      const deductionTotal = Number(pc.deductiontotalamount || 0);
      const released = Number(pc.deductionreleasedamount || 0);
      const pending = Number(pc.deductionpendingamount || 0);
      const deductionStatus = deductionTotal <= 0
        ? 'Not applicable'
        : pending <= 0.001
          ? 'Fully released'
          : released > 0
            ? 'Partially released'
            : 'Pending release';
      const sapStatus = pc.sapinvoicedocentry || pc.sapinvoicedocnum
        ? 'AP Invoice Posted'
        : pc.sapgrpodocentry || pc.sapgrpodocnum
          ? 'GRPO Linked'
          : 'Not Posted';
      return [
        pc.paymentnumber,
        pc.paymenttype,
        pc.createddate ? String(pc.createddate).substring(0, 10) : '',
        amount(pc.contractvalue),
        amount(pc.currentworkdoneamt),
        amount(pc.currentnetpayable),
        amount(pc.currentretentionamt),
        amount(pc.currentperformancebondamt),
        amount(pc.currentinsuranceamt),
        amount(pc.currentworkmenscompamt),
        amount(pc.currenttaxclearanceamt),
        amount(pc.currentadvanceguaranteeamt),
        amount(deductionTotal),
        amount(released),
        amount(pending),
        deductionStatus,
        pc.status,
        sapStatus,
        pc.sapgrpodocnum,
        pc.sapgrpodocentry,
        pc.sapinvoicedocnum,
        pc.sapinvoicedocentry,
        pc.sapgoodsissuedocnum,
        pc.sapgoodsissuedocentry,
        pc.createdbyname,
        pc.submittedbyname,
        pc.remarks,
      ];
    });
    const totalRow = [
      'TOTAL', '', '', amount(paymentCertificateSummary.contractValue), '',
      amount(paymentCerts.reduce((sum, pc) => sum + Number(pc.currentnetpayable || 0), 0)),
      '', '', '', '', '', '', amount(paymentCertificateSummary.totalDeducted),
      amount(paymentCertificateSummary.totalReleased), amount(paymentCertificateSummary.pendingDeductions),
    ];
    const clipboardText = [
      ...summaryRows,
      [],
      ['Payment Certificates'],
      headers,
      ...rows,
      totalRow,
    ].map((row) => row.map(clean).join('\t')).join('\r\n');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(clipboardText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = clipboardText;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      dispatch(showSnackbar({ message: `${paymentCerts.length} payment certificate row(s) copied for Excel`, severity: 'success' }));
    } catch (error) {
      dispatch(showSnackbar({ message: 'Unable to copy the payment certificate table', severity: 'error' }));
    }
  }, [dispatch, paymentCertificateSummary, paymentCerts]);

  // --- Helper: get field from selectedSC ---
  const scField = (field: string, fallback: string = ''): string => {
    if (!selectedSC) return fallback;
    return String(selectedSC[field] ?? selectedSC[field.toLowerCase()] ?? fallback);
  };

  const scBool = (field: string): boolean => {
    if (!selectedSC) return false;
    const val = selectedSC[field] ?? selectedSC[field.toLowerCase()];
    if (typeof val === 'boolean') return val;
    if (typeof val === 'number') return val !== 0;
    const normalized = String(val ?? '').trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 't' || normalized === 'y' || normalized === 'yes';
  };

  const scNumber = (field: string): string => {
    if (!selectedSC) return '';
    const val = selectedSC[field] ?? selectedSC[field.toLowerCase()];
    if (val === null || val === undefined) return '';
    return String(val);
  };

  // --- Determine if a document type is required based on subcontract flags ---
  const isDocTypeRequired = (docType: DocumentTypeKey): boolean => {
    const mapping: Record<DocumentTypeKey, string> = {
      PerformanceBond: 'performancebondrequired',
      AllRiskInsurance: 'allriskinsurancerequired',
      WorkmensCompInsurance: 'workmenscompinsurancerequired',
      TaxClearanceCert: 'taxclearancecertrequired',
      AdvancePaymentGuarantee: 'advancepaymentguaranteerequired',
      RetentionGuarantee: 'retentionguaranteerequired',
      SubcontractAgreement: 'subcontractagreementrequired',
      LetterOfAuthorization: 'letterofauthorizationrequired',
      CivilIDCopies: 'civilidcopiesrequired',
      BankAccountDetails: 'bankaccountdetailsrequired',
    };
    const field = mapping[docType];
    return scBool(field);
  };

  // ==================== RENDER ====================
  return (
    <Box component="section" aria-labelledby="sd-heading" sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Backdrop open={loadingSC} sx={{ zIndex: 1300 }}>
        <CircularProgress sx={{ color: '#F1C232' }} aria-label="Loading subcontract" />
      </Backdrop>

      {/* Page Title */}
      <Typography
        component="h1"
        id="sd-heading"
        variant="h5"
        sx={{ fontWeight: 700, mb: 2, color: '#1a237e', fontSize: { xs: 18, sm: 22, md: 24 } }}
      >
        Subcontract Documents
      </Typography>

      {/* ========== PART 1: Search Panel ========== */}
      <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
        <Typography sx={sectionHeaderSx}>Search Subcontracts</Typography>
        <Grid container spacing={2} alignItems="flex-end">
          {/* Project Number (searchable dropdown) */}
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              label="Project Number"
              size="small"
              fullWidth
              value={filterProjectCode}
              onClick={() => setProjectPickerOpen(true)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setProjectPickerOpen(true)}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                    {filterProjectCode && (
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFilterProjectCode(''); }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {/* Project Name (text) */}
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              label="Project Name"
              size="small"
              fullWidth
              value={filterProjectName}
              onChange={(e) => setFilterProjectName(e.target.value)}
            />
          </Grid>
          {/* Vendor Code (searchable dropdown) */}
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              label="Vendor Code"
              size="small"
              fullWidth
              value={filterVendorCode}
              onClick={() => setVendorPickerOpen(true)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setVendorPickerOpen(true)}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                    {filterVendorCode && (
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFilterVendorCode(''); setFilterVendorName(''); }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {/* Vendor Name (text) */}
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              label="Vendor Name"
              size="small"
              fullWidth
              value={filterVendorName}
              onChange={(e) => setFilterVendorName(e.target.value)}
            />
          </Grid>
          {/* Contract Number (text) */}
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              label="Contract Number"
              size="small"
              fullWidth
              value={filterContractNumber}
              onChange={(e) => setFilterContractNumber(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            />
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" sx={goldButtonSx} onClick={handleSearch} disabled={searching} startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}>
            Search
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setFilterProjectCode('');
              setFilterProjectName('');
              setFilterVendorCode('');
              setFilterVendorName('');
              setFilterContractNumber('');
              setSearchResults([]);
              setSelectedSC(null);
              setPaymentCerts([]);
            }}
          >
            Clear
          </Button>
        </Stack>
      </Paper>

      {/* ========== Search Results Table ========== */}
      {searchResults.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
          <Typography sx={sectionHeaderSx}>Search Results ({searchResults.length})</Typography>
          <Box className="ag-theme-alpine" sx={{ height: 280, width: '100%' }}>
            <AgGridReact
              rowData={searchResults}
              columnDefs={[
                { field: 'subcontractnumber', headerName: 'SC Number', width: 150, cellStyle: { cursor: 'pointer', color: '#1a237e', fontWeight: 600 } },
                { field: 'projectcode', headerName: 'Project', width: 130 },
                { field: 'vendorname', headerName: 'Vendor', flex: 1, minWidth: 180 },
                { field: 'contractnumber', headerName: 'Contract No', width: 150 },
                {
                  field: 'subcontractvalue',
                  headerName: 'Value',
                  width: 140,
                  valueFormatter: (p: any) => {
                    const v = Number(p.value || 0);
                    return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  },
                  cellStyle: { textAlign: 'right' },
                },
                {
                  field: 'status',
                  headerName: 'Status',
                  width: 120,
                  cellRenderer: (p: any) => {
                    const s = String(p.value || '');
                    return s;
                  },
                },
              ]}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              getRowId={(params: any) => String(params.data.subcontractid)}
              rowSelection="single"
              onRowClicked={(p: any) => {
                if (p.data?.subcontractid) {
                  if (String(p.data?.status || '').trim().toLowerCase() === 'draft') {
                    navigate(`/subcontract/create?draftId=${encodeURIComponent(String(p.data.subcontractid))}`);
                    return;
                  }
                  loadSubcontract(p.data.subcontractid);
                }
              }}
              rowStyle={{ cursor: 'pointer' }}
            />
          </Box>
        </Paper>
      )}

      {/* ========== PART 2: Selected Subcontract Detail (read-only) ========== */}
      {selectedSC && (
        <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={sectionHeaderSx}>
              Subcontract: {scField('subcontractnumber')}
            </Typography>
            <Chip
              label={scField('status', 'Draft')}
              color={statusChipColor(scField('status'))}
              size="small"
            />
          </Stack>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Row 1 */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Project Number" size="small" fullWidth value={scField('projectcode')} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Project Name" size="small" fullWidth value={scField('projectname')} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Contract Number" size="small" fullWidth value={scField('contractnumber')} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Subcontract Value" size="small" fullWidth value={scNumber('subcontractvalue')} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
            </Grid>

            {/* Row 2 */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Vendor/Subcontractor Code" size="small" fullWidth value={scField('vendorcode')} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Vendor/Subcontractor Name" size="small" fullWidth value={scField('vendorname')} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Start Date" size="small" fullWidth value={scField('contractstartdate') ? scField('contractstartdate').substring(0, 10) : ''} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Completion Date" size="small" fullWidth value={scField('contractcompletiondate') ? scField('contractcompletiondate').substring(0, 10) : ''} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
            </Grid>

            {/* Row 3 */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Currency" size="small" fullWidth value={scField('currency', 'KWD')} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Retention %" size="small" fullWidth value={scNumber('retentionpct')} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Advance Payment %" size="small" fullWidth value={scNumber('advancepaymentpct')} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
            </Grid>

            {/* Row 4: Flags and percentages */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography sx={{ ...sectionHeaderSx, mt: 1 }}>Required Documents & Percentages</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Performance Bond"
                size="small"
                fullWidth
                value={scBool('performancebondrequired') ? `Yes (${scNumber('performancebondpct')}%)` : 'No'}
                InputProps={{ readOnly: true }}
                sx={readOnlyFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="All Risk Insurance"
                size="small"
                fullWidth
                value={scBool('allriskinsurancerequired') ? `Yes (${scNumber('allriskinsurancepct')}%)` : 'No'}
                InputProps={{ readOnly: true }}
                sx={readOnlyFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Workmen's Compensation"
                size="small"
                fullWidth
                value={scBool('workmenscompinsurancerequired') ? `Yes (${scNumber('workmenscompinsurancepct')}%)` : 'No'}
                InputProps={{ readOnly: true }}
                sx={readOnlyFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Tax Clearance Cert"
                size="small"
                fullWidth
                value={scBool('taxclearancecertrequired') ? `Yes (${scNumber('taxclearancecertpct')}%)` : 'No'}
                InputProps={{ readOnly: true }}
                sx={readOnlyFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Advance Payment Guarantee"
                size="small"
                fullWidth
                value={scBool('advancepaymentguaranteerequired') ? `Yes (${scNumber('advancepaymentpct')}%)` : 'No'}
                InputProps={{ readOnly: true }}
                sx={readOnlyFieldSx}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* ========== PART 3: Document Received Section ========== */}
      {selectedSC && (
        <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
          <Typography sx={sectionHeaderSx}>Document Received</Typography>

          {DOCUMENT_TYPES.map((dt) => {
            const state = docStates[dt.key];
            const required = isDocTypeRequired(dt.key);
            const canUpload = state.received && required;

            return (
              <Paper
                key={dt.key}
                variant="outlined"
                sx={{ p: 2, mb: 1.5, bgcolor: required ? '#fff' : '#fafafa', borderColor: required ? '#e0e0e0' : '#eee' }}
              >
                <Grid container spacing={2} alignItems="center">
                  {/* Document type label */}
                  <Grid item xs={12} sm={3}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <DescriptionIcon fontSize="small" color={required ? 'primary' : 'disabled'} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {dt.label}
                      </Typography>
                      {required ? (
                        <Chip label="Required" size="small" variant="filled" color="error" sx={{ fontSize: '0.7rem', height: 20 }} />
                      ) : (
                        <Chip label="Optional" size="small" variant="outlined" color="default" sx={{ fontSize: '0.7rem', height: 20 }} />
                      )}
                    </Stack>
                  </Grid>

                  {/* Received toggle */}
                  <Grid item xs={6} sm={2}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="caption" color="textSecondary">Received:</Typography>
                      <Switch
                        checked={state.received}
                        onChange={(e) => handleDocumentReceivedToggle(dt.key, e.target.checked)}
                        size="small"
                        disabled={!required}
                        color="primary"
                      />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: state.received ? 'success.main' : 'text.secondary' }}>
                        {state.received ? 'Yes' : 'No'}
                      </Typography>
                    </Stack>
                  </Grid>

                  {/* Upload */}
                  <Grid item xs={6} sm={3}>
                    <input
                      type="file"
                      ref={(el) => { fileInputRefs.current[dt.key] = el; }}
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileUpload(dt.key, e.target.files)}
                      multiple
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={state.uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                      disabled={!canUpload || state.uploading}
                      onClick={() => fileInputRefs.current[dt.key]?.click()}
                    >
                      {state.uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </Grid>

                  {/* View attachments */}
                  <Grid item xs={12} sm={4}>
                    {state.attachments.length > 0 ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {state.attachments.map((att: any) => (
                          <Tooltip key={att.attachmentid} title={att.originalname || att.filename || 'View'}>
                            <Button
                              variant="text"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => {
                                const url = apiService.getDocumentAttachmentViewUrl(selectedSC.subcontractid, att.attachmentid);
                                window.open(url, '_blank');
                              }}
                              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            >
                              {String(att.originalname || att.filename || 'Attachment').substring(0, 25)}
                            </Button>
                          </Tooltip>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="textSecondary">No attachments</Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
        </Paper>
      )}

      {/* ========== PART 4: Payment Certificates List ========== */}
      {selectedSC && canViewPaymentCertificates && (
        <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
            <Typography sx={{ ...sectionHeaderSx, mb: 0, mt: 0 }}>Payment Certificates</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyPaymentCertificateTable}
              disabled={loadingPCs || paymentCerts.length === 0}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Copy Table
            </Button>
          </Stack>

          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            {[
              { label: 'Contract Value', value: paymentCertificateSummary.contractValue, color: '#eef3fb' },
              { label: 'Paid / Posted Amount', value: paymentCertificateSummary.paidAmount, color: '#e8f5e9' },
              { label: 'Pending Contract Amount', value: paymentCertificateSummary.pendingContractAmount, color: '#fff3e0' },
              { label: 'Total Withheld Deductions', value: paymentCertificateSummary.totalDeducted, color: '#fce4ec' },
              { label: 'Released Deductions', value: paymentCertificateSummary.totalReleased, color: '#e0f2f1' },
              { label: 'Pending Deduction Release', value: paymentCertificateSummary.pendingDeductions, color: '#fff8e1' },
            ].map((metric) => (
              <Grid item xs={6} sm={4} md={2} key={metric.label}>
                <Paper variant="outlined" sx={{ px: 1.25, py: 0.8, bgcolor: metric.color, height: '100%' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{metric.label}</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {formatCertificateAmount(metric.value)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {loadingPCs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : paymentCerts.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>No payment certificates found for this subcontract.</Alert>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ mt: 1, minWidth: 1680 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Payment No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Net Payable</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Deducted</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Released</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Pending Release</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Deduction Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>SAP Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>SAP GRPO</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>SAP AP Document</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>SAP Goods Issue</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentCerts.map((pc: any) => {
                  const pcStatus = String(pc.status || '');
                  const hasInvoiceEntry = !!(pc.sapinvoicedocentry || pc.sapinvoicedocnum);
                  const hasLinkedGRPO = !!(pc.sapgrpodocentry || pc.sapgrpodocnum);
                  const sapStatus = hasInvoiceEntry ? 'AP Invoice Posted' : (hasLinkedGRPO ? 'GRPO Linked' : 'Not Posted');
                  const canPostToSAP = pcStatus === 'SubmittedToFinance' && !hasInvoiceEntry;
                  const deductionTotal = Number(pc.deductiontotalamount || 0);
                  const deductionReleased = Number(pc.deductionreleasedamount || 0);
                  const deductionPending = Number(pc.deductionpendingamount || 0);
                  const deductionsComplete = deductionTotal > 0 && deductionPending <= 0.001;
                  const deductionsPartial = deductionReleased > 0 && deductionPending > 0.001;
                  const canOpenRelease = canReleaseDeductions
                    && pcStatus === 'Approved'
                    && hasInvoiceEntry
                    && String(pc.paymenttype || '').toUpperCase() === 'INTERIM'
                    && deductionTotal > 0;

                  return (
                    <TableRow
                      key={pc.paymentcertificateid}
                      hover
                      sx={{
                        bgcolor: deductionsComplete ? '#e8f5e9' : deductionsPartial ? '#fff8e1' : 'inherit',
                        '&:hover': { bgcolor: deductionsComplete ? '#dcedc8' : deductionsPartial ? '#ffecb3' : '#f5f9ff' },
                      }}
                    >
                      <TableCell>
                        <Button
                          variant="text"
                          size="small"
                          sx={{ textTransform: 'none', p: 0, minWidth: 'auto', fontWeight: 600 }}
                          onClick={() => handleOpenPCDetail(pc.paymentcertificateid, selectedSC?.subcontractid)}
                        >
                          {pc.paymentnumber || `PC-${pc.paymentcertificateid}`}
                        </Button>
                      </TableCell>
                      <TableCell>{pc.paymenttype || '-'}</TableCell>
                      <TableCell>
                        {pc.createddate ? String(pc.createddate).substring(0, 10) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {Number(pc.currentnetpayable || pc.netpayable || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="right">{formatCertificateAmount(deductionTotal)}</TableCell>
                      <TableCell align="right">{formatCertificateAmount(deductionReleased)}</TableCell>
                      <TableCell align="right">{formatCertificateAmount(deductionPending)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={deductionTotal <= 0 ? 'Not applicable' : deductionsComplete ? 'Fully released' : deductionsPartial ? 'Partially released' : 'Pending release'}
                          color={deductionsComplete ? 'success' : deductionsPartial ? 'warning' : deductionTotal > 0 ? 'error' : 'default'}
                          variant={deductionTotal > 0 ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={pcStatus || 'Draft'}
                          size="small"
                          color={pcStatus === 'SubmittedToFinance' ? 'primary' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sapStatus}
                          size="small"
                          color={hasInvoiceEntry ? 'success' : (hasLinkedGRPO ? 'info' : 'default')}
                          variant={(hasInvoiceEntry || hasLinkedGRPO) ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        {(pc.sapgrpodocnum || pc.sapgrpodocentry)
                          ? `DocNum ${pc.sapgrpodocnum || '-'} / DocEntry ${pc.sapgrpodocentry || '-'}`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {(pc.sapinvoicedocnum || pc.sapinvoicedocentry)
                          ? `DocNum ${pc.sapinvoicedocnum || '-'} / DocEntry ${pc.sapinvoicedocentry || '-'}`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {(pc.sapgoodsissuedocnum || pc.sapgoodsissuedocentry)
                          ? `DocNum ${pc.sapgoodsissuedocnum || '-'} / DocEntry ${pc.sapgoodsissuedocentry || '-'}`
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          {canPostToSAP && (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<SendIcon />}
                              sx={{ ...goldButtonSx, fontSize: '0.75rem', py: 0.3, px: 1.5 }}
                              onClick={() => {
                                setSapPostTarget({ scId: selectedSC.subcontractid, pcId: pc.paymentcertificateid });
                                setSapPostConfirmOpen(true);
                              }}
                            >
                              Send to SAP
                            </Button>
                          )}
                          {canOpenRelease && (
                            <Button
                              variant={deductionsComplete ? 'contained' : 'outlined'}
                              color={deductionsComplete ? 'success' : 'primary'}
                              size="small"
                              startIcon={<PriceCheckIcon />}
                              sx={{ fontSize: '0.75rem', py: 0.3, px: 1.2, whiteSpace: 'nowrap' }}
                              onClick={() => handleOpenDeductionRelease(selectedSC.subcontractid, pc.paymentcertificateid)}
                            >
                              {deductionsComplete ? 'View Releases' : deductionsPartial ? 'Release Balance' : 'Release Deductions'}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ bgcolor: '#eceff1', '& td': { fontWeight: 700 } }}>
                  <TableCell colSpan={3}>Total</TableCell>
                  <TableCell align="right">{formatCertificateAmount(paymentCerts.reduce((sum, pc) => sum + Number(pc.currentnetpayable || 0), 0))}</TableCell>
                  <TableCell align="right">{formatCertificateAmount(paymentCertificateSummary.totalDeducted)}</TableCell>
                  <TableCell align="right">{formatCertificateAmount(paymentCertificateSummary.totalReleased)}</TableCell>
                  <TableCell align="right">{formatCertificateAmount(paymentCertificateSummary.pendingDeductions)}</TableCell>
                  <TableCell colSpan={7} />
                </TableRow>
              </TableBody>
            </Table>
            </Box>
          )}
        </Paper>
      )}

      {/* ========== DIALOGS ========== */}

      {/* Project Picker Dialog */}
      <Dialog open={projectPickerOpen} onClose={() => { setProjectPickerOpen(false); setProjectSearchQuery(''); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Select Project
            <IconButton onClick={() => { setProjectPickerOpen(false); setProjectSearchQuery(''); }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search projects..."
            value={projectSearchQuery}
            onChange={(e) => setProjectSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} />
            }}
          />
          <Box className="ag-theme-alpine" sx={{ height: 360, width: '100%' }}>
            <AgGridReact
              rowData={filteredProjectOptions.slice(0, 200)}
              columnDefs={[
                { field: 'Code', headerName: 'Code', flex: 1 },
                { field: 'Name', headerName: 'Name', flex: 2 },
              ]}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              onRowClicked={(p: any) => {
                const row = p.data;
                setFilterProjectCode(row.Code || row.code || '');
                setFilterProjectName(row.Name || row.name || '');
                setProjectPickerOpen(false);
                setProjectSearchQuery('');
              }}
              onRowDoubleClicked={(p: any) => {
                const row = p.data;
                setFilterProjectCode(row.Code || row.code || '');
                setFilterProjectName(row.Name || row.name || '');
                setProjectPickerOpen(false);
                setProjectSearchQuery('');
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Vendor Picker Dialog */}
      <Dialog open={vendorPickerOpen} onClose={() => { setVendorPickerOpen(false); setVendorSearchQuery(''); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Select Vendor
            <IconButton onClick={() => { setVendorPickerOpen(false); setVendorSearchQuery(''); }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search vendors..."
            value={vendorSearchQuery}
            onChange={(e) => setVendorSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} />
            }}
          />
          <Box className="ag-theme-alpine" sx={{ height: 360, width: '100%' }}>
            <AgGridReact
              rowData={filteredVendorOptions.slice(0, 200)}
              columnDefs={[
                { field: 'CardCode', headerName: 'Code', flex: 1 },
                { field: 'CardName', headerName: 'Name', flex: 2 },
              ]}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              onRowClicked={(p: any) => {
                const row = p.data;
                setFilterVendorCode(row.CardCode || row.cardcode || '');
                setFilterVendorName(row.CardName || row.cardname || '');
                setVendorPickerOpen(false);
                setVendorSearchQuery('');
              }}
              onRowDoubleClicked={(p: any) => {
                const row = p.data;
                setFilterVendorCode(row.CardCode || row.cardcode || '');
                setFilterVendorName(row.CardName || row.cardname || '');
                setVendorPickerOpen(false);
                setVendorSearchQuery('');
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Payment Certificate Detail Dialog */}
      <Dialog
        open={pcDetailOpen}
        onClose={() => { setPcDetailOpen(false); setPcDetailData(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">
            Payment Certificate — {pcDetailData?.paymentnumber || ''}
          </Typography>
          <IconButton onClick={() => { setPcDetailOpen(false); setPcDetailData(null); }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {pcDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : pcDetailData ? (() => {
            const pc = pcDetailData;
            const pf = (v: any) => parseFloat(v || 0);
            const fmt = (v: any) => Number(pf(v)).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
            const fmtPct = (v: any) => `${pf(v).toFixed(2)}%`;
            const fmtDate = (v: any) => v ? String(v).substring(0, 10) : '-';
            const isAdvanced = String(pc.paymenttype || '').toLowerCase() === 'advanced';

            const retentionPct = pf(pc.retentionpct);
            const advRecoveryPct = pf(pc.advancerecoverypct || pc.advancepaymentpct);
            // Fallback: for PCs saved before deduction columns existed, derive from subcontract %
            const scWCPct = pf(selectedSC?.workmenscompinsurancepct ?? selectedSC?.workmenscompensationpct);
            const scInsPct = pf(selectedSC?.allriskinsurancepct);
            const scTCPct = pf(selectedSC?.taxclearancecertpct);
            // IMPORTANT: Use != null check, NOT ||, because 0 is a valid value
            // (e.g. when a document like Workmen's Comp cert is received, cumulative becomes 0 intentionally)
            const workmensCompPct = pc.workmenscomppct != null ? pf(pc.workmenscomppct) : scWCPct;
            const insurancePct = pc.insurancepct != null ? pf(pc.insurancepct) : scInsPct;
            const taxClearancePct = pc.taxclearancepct != null ? pf(pc.taxclearancepct) : scTCPct;
            const gross = pf(pc.grossvaluation);
            // Cumulative amounts — fallback only when column doesn't exist (null/undefined), not when intentionally 0
            const wcAmt = pc.workmenscompamt != null ? pf(pc.workmenscompamt) : (gross * scWCPct / 100);
            const insAmt = pc.insuranceamt != null ? pf(pc.insuranceamt) : (gross * scInsPct / 100);
            const tcAmt = pc.taxclearanceamt != null ? pf(pc.taxclearanceamt) : (gross * scTCPct / 100);
            // Current amounts — fallback only when column doesn't exist
            const wcCurr = pc.currentworkmenscompamt != null ? pf(pc.currentworkmenscompamt) : (wcAmt - pf(pc.previousworkmenscompamt));
            const insCurr = pc.currentinsuranceamt != null ? pf(pc.currentinsuranceamt) : (insAmt - pf(pc.previousinsuranceamt));
            const tcCurr = pc.currenttaxclearanceamt != null ? pf(pc.currenttaxclearanceamt) : (tcAmt - pf(pc.previoustaxclearanceamt));
            // Previous amounts — fallback: for the very first interim PC, previous is 0;
            // for later PCs, recalculate from previous PC's grossvaluation if available
            const wcPrev = pf(pc.previousworkmenscompamt);
            const insPrev = pf(pc.previousinsuranceamt);
            const tcPrev = pf(pc.previoustaxclearanceamt);

            return (
              <Box>
                {/* Header Info */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}><Typography variant="caption" color="text.secondary">Subcontract</Typography><Typography variant="body2" fontWeight={600}>{pc.subcontractnumber || '-'}</Typography></Grid>
                  <Grid item xs={4}><Typography variant="caption" color="text.secondary">Vendor</Typography><Typography variant="body2" fontWeight={600}>{pc.vendorname || '-'}</Typography></Grid>
                  <Grid item xs={4}><Typography variant="caption" color="text.secondary">Project</Typography><Typography variant="body2" fontWeight={600}>{pc.projectcode || '-'}</Typography></Grid>
                  <Grid item xs={3}><Typography variant="caption" color="text.secondary">Payment No</Typography><Typography variant="body2">{pc.paymentnumber || '-'}</Typography></Grid>
                  <Grid item xs={3}><Typography variant="caption" color="text.secondary">Type</Typography><Typography variant="body2">{pc.paymenttype || '-'}</Typography></Grid>
                  <Grid item xs={3}><Typography variant="caption" color="text.secondary">Status</Typography><Chip label={pc.status || 'Draft'} size="small" color={pc.status === 'SubmittedToFinance' ? 'primary' : 'warning'} variant="outlined" /></Grid>
                  <Grid item xs={3}><Typography variant="caption" color="text.secondary">Work Done Up To</Typography><Typography variant="body2">{fmtDate(pc.workdoneupto)}</Typography></Grid>
                  {pc.invoiceno && <Grid item xs={3}><Typography variant="caption" color="text.secondary">Invoice No</Typography><Typography variant="body2">{pc.invoiceno}</Typography></Grid>}
                  {pc.invoicedate && <Grid item xs={3}><Typography variant="caption" color="text.secondary">Invoice Date</Typography><Typography variant="body2">{fmtDate(pc.invoicedate)}</Typography></Grid>}
                  <Grid item xs={3}><Typography variant="caption" color="text.secondary">Contract Value</Typography><Typography variant="body2">{fmt(pc.contractvalue)}</Typography></Grid>
                  <Grid item xs={3}><Typography variant="caption" color="text.secondary">Revised Value</Typography><Typography variant="body2">{fmt(pc.revisedcontractvalue)}</Typography></Grid>
                </Grid>

                {isAdvanced ? (
                  /* Advanced PC — simple display */
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Advance Payment</Typography>
                    <Typography variant="h6">{fmt(pc.netpayable)} KWD</Typography>
                  </Paper>
                ) : (
                  /* Interim PC — finance evaluation table */
                  <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 1, fontSize: '0.8rem' } }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Cumulative (KWD)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Previous (KWD)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Net (KWD)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Work Done (A)</TableCell>
                        <TableCell align="right">{fmt(pc.cumulativeworkdoneamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.previouscumulativeworkdoneamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.currentworkdoneamt)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>MOS</TableCell>
                        <TableCell align="right">{fmt(pc.materialsonsiteamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.previousmaterialsonsiteamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.currentmaterialsonsiteamt)}</TableCell>
                      </TableRow>
                      <TableRow sx={{ borderTop: '2px solid #ccc' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Deduction</TableCell>
                        <TableCell align="right" />
                        <TableCell align="right" />
                        <TableCell align="right" />
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ pl: 3 }}>Retention ({fmtPct(retentionPct)})</TableCell>
                        <TableCell align="right">{fmt(pc.retentionamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.previousretentionamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.currentretentionamt)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ pl: 3 }}>Adv Payment Recovery ({fmtPct(advRecoveryPct)})</TableCell>
                        <TableCell align="right">{fmt(pc.advancerecoveryamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.previousadvancerecoveryamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.currentadvancerecoveryamt)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ pl: 3 }}>Workmen's Comp ({fmtPct(workmensCompPct)})</TableCell>
                        <TableCell align="right">{fmt(wcAmt)}</TableCell>
                        <TableCell align="right">{fmt(wcPrev)}</TableCell>
                        <TableCell align="right">{fmt(wcCurr)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ pl: 3 }}>Insurance ({fmtPct(insurancePct)})</TableCell>
                        <TableCell align="right">{fmt(insAmt)}</TableCell>
                        <TableCell align="right">{fmt(insPrev)}</TableCell>
                        <TableCell align="right">{fmt(insCurr)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ pl: 3 }}>Tax Clearance ({fmtPct(taxClearancePct)})</TableCell>
                        <TableCell align="right">{fmt(tcAmt)}</TableCell>
                        <TableCell align="right">{fmt(tcPrev)}</TableCell>
                        <TableCell align="right">{fmt(tcCurr)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ pl: 3 }}>Material Back Charges</TableCell>
                        <TableCell align="right">{fmt(pc.materialbackchargesamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.previousmaterialbackchargesamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.currentmaterialbackchargesamt)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ pl: 3 }}>Manpower Back Charges</TableCell>
                        <TableCell align="right">{fmt(pc.manpowerbackchargesamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.previousmanpowerbackchargesamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.currentmanpowerbackchargesamt)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ pl: 3 }}>Equipment Back Charges</TableCell>
                        <TableCell align="right">{fmt(pc.equipmentbackchargesamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.previousequipmentbackchargesamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.currentequipmentbackchargesamt)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ pl: 3 }}>Others</TableCell>
                        <TableCell align="right">{fmt(pc.othersamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.previousothersamt)}</TableCell>
                        <TableCell align="right">{fmt(pc.currentothersamt)}</TableCell>
                      </TableRow>
                      <TableRow sx={{ borderTop: '2px solid #333', backgroundColor: '#fafafa' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Total Deduction (B)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {fmt(pf(pc.retentionamt) + pf(pc.advancerecoveryamt) + wcAmt + insAmt + tcAmt + pf(pc.materialbackchargesamt) + pf(pc.manpowerbackchargesamt) + pf(pc.equipmentbackchargesamt) + pf(pc.othersamt))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {fmt(pf(pc.previousretentionamt) + pf(pc.previousadvancerecoveryamt) + wcPrev + insPrev + tcPrev + pf(pc.previousmaterialbackchargesamt) + pf(pc.previousmanpowerbackchargesamt) + pf(pc.previousequipmentbackchargesamt) + pf(pc.previousothersamt))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {fmt(pf(pc.currentretentionamt) + pf(pc.currentadvancerecoveryamt) + wcCurr + insCurr + tcCurr + pf(pc.currentmaterialbackchargesamt) + pf(pc.currentmanpowerbackchargesamt) + pf(pc.currentequipmentbackchargesamt) + pf(pc.currentothersamt))}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#f0f4ff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Payment (C = A - B)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(pc.netpayable)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(pc.previousnetpayable)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: pf(pc.currentnetpayable) < 0 ? 'red' : 'inherit' }}>{fmt(pc.currentnetpayable)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}

                {/* SAP References */}
                {(pc.sapgrpodocnum || pc.sapinvoicedocnum || pc.sapgoodsissuedocnum) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>SAP References</Typography>
                    <Grid container spacing={2}>
                      {pc.sapgrpodocnum && <Grid item xs={4}><Typography variant="caption" color="text.secondary">GRPO</Typography><Typography variant="body2">DocNum {pc.sapgrpodocnum} / DocEntry {pc.sapgrpodocentry || '-'}</Typography></Grid>}
                      {pc.sapinvoicedocnum && <Grid item xs={4}><Typography variant="caption" color="text.secondary">AP Invoice</Typography><Typography variant="body2">DocNum {pc.sapinvoicedocnum} / DocEntry {pc.sapinvoicedocentry || '-'}</Typography></Grid>}
                      {pc.sapgoodsissuedocnum && <Grid item xs={4}><Typography variant="caption" color="text.secondary">Goods Issue</Typography><Typography variant="body2">DocNum {pc.sapgoodsissuedocnum} / DocEntry {pc.sapgoodsissuedocentry || '-'}</Typography></Grid>}
                    </Grid>
                  </Box>
                )}
              </Box>
            );
          })() : null}
        </DialogContent>
      </Dialog>

      {/* Finance deduction release dialog */}
      <Dialog
        open={releaseDialogOpen}
        onClose={() => { if (!releasePosting) { setReleaseDialogOpen(false); setReleaseTarget(null); } }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Release Withheld Deductions</Typography>
              <Typography variant="caption" color="text.secondary">
                {releasePreview ? `${releasePreview.paymentNumber} | ${releasePreview.vendorCode} - ${releasePreview.vendorName}` : 'Loading payment certificate...'}
              </Typography>
            </Box>
            <IconButton disabled={releasePosting} onClick={() => { setReleaseDialogOpen(false); setReleaseTarget(null); }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
          {releaseLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={30} /></Box>
          ) : releasePreview ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" color="primary" label={`Selected release ${selectedReleaseTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
              </Stack>

              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                      <TableCell padding="checkbox">
                        <Tooltip title="Select or clear all remaining deductions">
                          <Checkbox
                            size="small"
                            checked={releaseLines.some((line) => line.remainingAmount > 0)
                              && releaseLines.filter((line) => line.remainingAmount > 0).every((line) => line.selected)}
                            indeterminate={releaseLines.some((line) => line.selected)
                              && !releaseLines.filter((line) => line.remainingAmount > 0).every((line) => line.selected)}
                            disabled={releasePosting || !releaseLines.some((line) => line.remainingAmount > 0)}
                            onChange={(event) => setReleaseLines((current) => current.map((line) => ({
                              ...line,
                              selected: line.remainingAmount > 0 ? event.target.checked : false,
                            })))}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>G/L Account</TableCell>
                      <TableCell align="right">Withheld</TableCell>
                      <TableCell align="right">Released</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                      <TableCell align="right" sx={{ width: 170 }}>Release Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {releaseLines.map((line, index) => (
                      <TableRow key={line.key} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={line.selected}
                            disabled={line.remainingAmount <= 0 || releasePosting}
                            onChange={(event) => setReleaseLines((current) => current.map((item, rowIndex) =>
                              rowIndex === index ? { ...item, selected: event.target.checked } : item
                            ))}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{line.itemCode}</Typography>
                          <Typography variant="caption" color="text.secondary">{line.itemName}</Typography>
                        </TableCell>
                        <TableCell>{line.accountCode}</TableCell>
                        <TableCell align="right">{line.withheldAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell align="right">{line.releasedAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell align="right">{line.remainingAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={line.releaseAmount}
                            disabled={!line.selected || releasePosting}
                            error={line.selected && (line.releaseAmount <= 0 || line.releaseAmount > line.remainingAmount)}
                            inputProps={{ min: 0.001, max: line.remainingAmount, step: 0.001, style: { textAlign: 'right' } }}
                            onChange={(event) => setReleaseLines((current) => current.map((item, rowIndex) =>
                              rowIndex === index ? { ...item, releaseAmount: Number(event.target.value) } : item
                            ))}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {releasePreview.history?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Release History</Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 650 }}>
                      <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Status</TableCell><TableCell>SAP AP Invoice</TableCell><TableCell align="right">Released</TableCell><TableCell>Created By</TableCell></TableRow></TableHead>
                      <TableBody>
                        {releasePreview.history.map((entry: any) => (
                          <TableRow key={entry.releaseid}>
                            <TableCell>{entry.posteddate ? new Date(entry.posteddate).toLocaleString() : new Date(entry.createddate).toLocaleString()}</TableCell>
                            <TableCell><Chip size="small" label={entry.status} color={entry.status === 'Posted' ? 'success' : entry.status === 'Failed' ? 'error' : 'warning'} /></TableCell>
                            <TableCell>{entry.sappurchasedocnum ? `DocNum ${entry.sappurchasedocnum} / DocEntry ${entry.sappurchasedocentry || '-'}` : '-'}</TableCell>
                            <TableCell align="right">{Number(entry.totalreleaseamount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell>{entry.createdbyname || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={releasePosting} onClick={() => { setReleaseDialogOpen(false); setReleaseTarget(null); }}>Cancel</Button>
          <Button
            variant="contained"
            sx={goldButtonSx}
            disabled={releaseLoading || releasePosting || selectedReleaseTotal <= 0 || releaseLines.some((line) => line.selected && (line.releaseAmount <= 0 || line.releaseAmount > line.remainingAmount))}
            startIcon={releasePosting ? <CircularProgress size={16} /> : <PriceCheckIcon />}
            onClick={handlePostDeductionRelease}
          >
            {releasePosting ? 'Posting one AP invoice...' : `Post One AP Invoice (${releaseLines.filter((line) => line.selected).length} lines)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SAP Post Confirmation Dialog */}
      <Dialog open={sapPostConfirmOpen} onClose={() => { if (!sapPosting) { setSapPostConfirmOpen(false); setSapPostTarget(null); } }}>
        <DialogTitle>Confirm Post to SAP</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to post this payment certificate to SAP? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSapPostConfirmOpen(false); setSapPostTarget(null); }} disabled={sapPosting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={goldButtonSx}
            onClick={handleSAPPost}
            disabled={sapPosting}
            startIcon={sapPosting ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {sapPosting ? 'Posting...' : 'Confirm & Post'}
          </Button>
        </DialogActions>
      </Dialog>
      <ActionStatusDialog
        {...actionStatus}
        onClose={() => setActionStatus((current) => ({ ...current, open: false }))}
      />
    </Box>
  );
};

export default SubcontractDocuments;
